import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BLUE } from './constants';
import { alertStyle, buttonStyle } from './changeOrderStyles';
import ChangeOrderNativeEditor from './ChangeOrderNativeEditor';
import ChangeOrderRecords from './ChangeOrderRecords';
import UploadChangeOrderForm from './UploadChangeOrderForm';

const panelStyle = {
  marginTop: 22,
  padding: 20,
  background: '#fbfcff',
  border: '1px solid #dbe3ef',
  borderRadius: 12,
  color: '#26364d',
};

function formatError(error, fallback) {
  return error?.message || fallback;
}

function parseInitialPages(value) {
  if (!value.trim()) return [];
  const pages = value.split(',').map((part) => Number(part.trim()));
  if (pages.some((page) => !Number.isInteger(page) || page < 1)) {
    throw new Error('Enter positive page numbers separated by commas.');
  }
  return [...new Set(pages)];
}

export default function JobChangeOrders({ job, token, importedContracts = [] }) {
  const jobId = job?.id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState('');
  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState('');
  const [title, setTitle] = useState('');
  const [changeNumber, setChangeNumber] = useState('');
  const [initialPages, setInitialPages] = useState('');
  const [uploading, setUploading] = useState(false);
  const [revisionTarget, setRevisionTarget] = useState(null);
  const [previews, setPreviews] = useState({});
  const previewUrls = useRef([]);
  const loadSequence = useRef(0);

  const authHeaders = useMemo(() => (token ? { 'x-auth-token': token } : {}), [token]);

  const loadItems = useCallback(async () => {
    const sequence = ++loadSequence.current;
    if (!jobId) {
      setLoading(false);
      setItems([]);
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const response = await fetch(`/api/jobs/${encodeURIComponent(jobId)}/change-orders`, {
        headers: authHeaders,
      });
      if (!response.headers.get('content-type')?.includes('application/json')) {
        throw new Error(
          'The change-order service is unavailable. Refresh after the app update completes.',
        );
      }
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Unable to load change orders.');
      if (sequence !== loadSequence.current) return;
      setItems(Array.isArray(payload.change_orders) ? payload.change_orders : []);
      previewUrls.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrls.current = [];
      setPreviews({});
    } catch (error) {
      if (sequence === loadSequence.current)
        setLoadError(formatError(error, 'Unable to load change orders.'));
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, [jobId, authHeaders]);

  useEffect(() => {
    loadItems();
    return () => {
      loadSequence.current++;
    };
  }, [loadItems]);

  const priorJobId = useRef(jobId);
  useEffect(() => {
    if (priorJobId.current === jobId) return;
    priorJobId.current = jobId;
    previewUrls.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrls.current = [];
    setPreviews({});
    setItems([]);
    setActionError('');
    setLoadError('');
    setBusyId(null);
    setUploading(false);
    setFile(null);
    setTitle('');
    setChangeNumber('');
    setInitialPages('');
    setRevisionTarget(null);
  }, [jobId]);

  useEffect(() => {
    if (!file) {
      setFileUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => () => previewUrls.current.forEach((url) => URL.revokeObjectURL(url)), []);

  const request = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: { ...authHeaders, ...(options.headers || {}) },
    });
    if (!response.headers.get('content-type')?.includes('application/json')) {
      throw new Error(
        'The change-order service is unavailable. Your input has been kept; refresh after updating the app.',
      );
    }
    const payload = await response.json();
    if (!response.ok)
      throw new Error(payload.error || payload.message || 'Request failed. Please try again.');
    return payload;
  };

  const uploadDraft = async (event) => {
    event.preventDefault();
    setActionError('');
    if (!file || file.type !== 'application/pdf') {
      setActionError('Choose a PDF document to continue.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setActionError('The PDF must be 10 MB or smaller.');
      return;
    }
    let parsedPages;
    try {
      parsedPages = parseInitialPages(initialPages);
    } catch (error) {
      setActionError(error.message);
      return;
    }
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('title', title.trim());
    formData.append('change_number', changeNumber.trim());
    formData.append('initial_pages', JSON.stringify(parsedPages));
    setUploading(true);
    try {
      await request(`/api/jobs/${encodeURIComponent(jobId)}/change-orders`, {
        method: 'POST',
        body: formData,
      });
      setFile(null);
      setTitle('');
      setChangeNumber('');
      setInitialPages('');
      await loadItems();
    } catch (error) {
      setActionError(formatError(error, 'Unable to create the draft.'));
    } finally {
      setUploading(false);
    }
  };

  const openPreview = async (item) => {
    setActionError('');
    const current = previews[item.id];
    if (current) {
      window.open(current.url, '_blank', 'noopener,noreferrer');
      return;
    }
    setBusyId(item.id);
    try {
      const response = await fetch(
        `/api/jobs/${encodeURIComponent(jobId)}/change-orders/${encodeURIComponent(item.id)}/file`,
        { headers: authHeaders },
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Unable to retrieve the source PDF.');
      }
      const url = URL.createObjectURL(await response.blob());
      previewUrls.current.push(url);
      setPreviews((existing) => ({
        ...existing,
        [item.id]: { url, name: item.title || 'Change order PDF' },
      }));
    } catch (error) {
      setActionError(formatError(error, 'Unable to retrieve the source PDF.'));
    } finally {
      setBusyId(null);
    }
  };

  const downloadSource = (item) => {
    const preview = previews[item.id];
    if (!preview) return;
    const link = document.createElement('a');
    link.href = preview.url;
    link.download = `${(item.change_number || item.title || 'change-order').replace(/[^a-z0-9-_]+/gi, '_')}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const customer = job?.customer_name || 'Customer not listed';
  const address =
    [job?.project_address, job?.project_city, job?.project_state].filter(Boolean).join(', ') ||
    'Address not listed';

  return (
    <section style={panelStyle} aria-labelledby="job-change-orders-title">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h3 id="job-change-orders-title" style={{ color: BLUE, margin: 0, fontSize: 18 }}>
            Change orders
          </h3>
          <p style={{ color: '#6b778a', fontSize: 13, margin: '5px 0 0' }}>
            Create a change order here or upload a complete PDF, then review and send it for
            signature.
          </p>
        </div>
        <button type="button" onClick={loadItems} disabled={loading} style={buttonStyle('quiet')}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {actionError && (
        <div role="alert" style={alertStyle}>
          {actionError}
        </div>
      )}
      {loadError && (
        <div role="alert" style={alertStyle}>
          {loadError}{' '}
          <button type="button" onClick={loadItems} style={textButtonStyle}>
            Retry
          </button>
        </div>
      )}

      <UploadChangeOrderForm
        file={file}
        fileUrl={fileUrl}
        title={title}
        changeNumber={changeNumber}
        initialPages={initialPages}
        uploading={uploading}
        loading={loading}
        customer={customer}
        address={address}
        jobId={jobId}
        onSubmit={uploadDraft}
        onFileChange={setFile}
        onTitleChange={setTitle}
        onChangeNumberChange={setChangeNumber}
        onInitialPagesChange={setInitialPages}
      />

      <ChangeOrderNativeEditor
        key={`native-${jobId}`}
        jobId={jobId}
        token={token}
        items={items}
        editOrder={revisionTarget}
        onEditConsumed={() => setRevisionTarget(null)}
        onSaved={loadItems}
      />
      {loading && !items.length ? (
        <div aria-label="Loading change orders" style={{ display: 'grid', gap: 9, marginTop: 20 }}>
          {[0, 1].map((key) => (
            <div
              key={key}
              style={{
                height: 60,
                borderRadius: 8,
                background: 'linear-gradient(90deg,#edf1f7,#f7f9fc,#edf1f7)',
              }}
            />
          ))}
        </div>
      ) : (
        <ChangeOrderRecords
          key={`records-${jobId}`}
          items={items}
          jobId={jobId}
          customer={customer}
          address={address}
          customerEmail={job?.customer_email}
          importedContracts={importedContracts}
          busyId={busyId}
          setBusyId={setBusyId}
          request={request}
          loadItems={loadItems}
          openPreview={openPreview}
          previews={previews}
          downloadSource={downloadSource}
          setActionError={setActionError}
          onEditRevision={setRevisionTarget}
        />
      )}
    </section>
  );
}

const textButtonStyle = {
  border: 0,
  background: 'transparent',
  color: '#1b3a6b',
  textDecoration: 'underline',
  cursor: 'pointer',
};
